import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { SidebarMenu } from "@arcadeai/design-system/components/ui/atoms/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { ChatHistoryItem } from "@arcadeai/design-system/components/ui/molecules/chat-history-item";
import { ConfirmPopover } from "@arcadeai/design-system/components/ui/molecules/confirm-popover";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2Icon } from "lucide-react";
import { Component, type ElementType, type ReactNode, Suspense } from "react";
import { useChatRuntime } from "../context";
import { useChatHistoryGroups } from "../hooks/use-chat-history-groups";
import { chatHistoryQueryOptions } from "../query-options";

export type ChatHistoryListProps = {
  baseUrl: string;
  viewerId: string;
  onDeleteChat: (id: string) => void;
  onItemSelect?: () => void;
  buildItemHref: (chatId: string) => string;
  linkComponent: ElementType;
  className?: string;
  groupLabelClassName?: string;
};

type DeleteAllActionProps = {
  baseUrl: string;
  viewerId: string;
  onDeleteAll: () => void;
};

/** biome-ignore lint/style/useReactFunctionComponents: Error boundaries require a class component in React */
class DeleteAllErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

function DeleteAllButton({
  baseUrl,
  viewerId,
  onDeleteAll,
}: DeleteAllActionProps) {
  const chat = useChatRuntime();
  const { groups } = useChatHistoryGroups({
    baseUrl,
    viewerId,
    chatId: chat.chatId,
  });

  if (groups.length === 0) {
    return null;
  }

  return (
    <Tooltip>
      <ConfirmPopover
        confirmLabel="Delete all"
        description="This action cannot be undone."
        onConfirm={onDeleteAll}
        title="Delete all chats?"
      >
        <TooltipTrigger
          render={
            <Button
              aria-label="Delete all chats"
              className="size-7 shrink-0 text-muted-foreground"
              size="icon"
              variant="ghost"
            />
          }
        >
          <Trash2Icon className="size-3.5" />
        </TooltipTrigger>
      </ConfirmPopover>
      <TooltipContent side="bottom">Delete all chats</TooltipContent>
    </Tooltip>
  );
}

/**
 * Self-contained delete-all button with built-in Suspense and error handling.
 * Suspends on the history query and renders nothing when there are no chats
 * or when the query fails.
 */
export function DeleteAllAction(props: DeleteAllActionProps) {
  return (
    <DeleteAllErrorBoundary>
      <Suspense>
        <DeleteAllButton {...props} />
      </Suspense>
    </DeleteAllErrorBoundary>
  );
}

function ChatHistoryEmpty({ children }: { children?: ReactNode }) {
  return (
    <p className="px-2 py-2 text-muted-foreground text-xs">
      {children ?? "No chats yet. Start a conversation!"}
    </p>
  );
}

export function ChatHistoryError({
  baseUrl,
  viewerId,
  onReset,
}: {
  baseUrl: string;
  viewerId: string;
  onReset?: () => void;
}) {
  const queryClient = useQueryClient();
  const historyQueryKey = chatHistoryQueryOptions({
    baseUrl,
    viewerId,
  }).queryKey;

  return (
    <>
      <p className="px-2 py-2 text-muted-foreground text-xs">
        Couldn't load history.
      </p>
      <Button
        className="h-7 text-xs"
        onClick={() => {
          queryClient
            .resetQueries({ queryKey: historyQueryKey })
            .finally(onReset);
        }}
        size="sm"
        variant="ghost"
      >
        Try again
      </Button>
    </>
  );
}

/**
 * Renders grouped chat history items. Must be used inside a ChatProvider
 * and wrapped in a Suspense boundary by the consumer.
 */
export function ChatHistoryList({
  baseUrl,
  viewerId,
  onDeleteChat,
  onItemSelect,
  buildItemHref,
  linkComponent,
  className,
  groupLabelClassName = "mb-2 px-3 font-medium text-muted-foreground text-xs",
}: ChatHistoryListProps) {
  const chat = useChatRuntime();
  const { groups } = useChatHistoryGroups({
    baseUrl,
    viewerId,
    chatId: chat.chatId,
  });

  if (groups.length === 0) {
    return <ChatHistoryEmpty />;
  }

  return (
    <div className={className}>
      {groups.map(({ label, items }) => (
        <div key={label}>
          <p className={groupLabelClassName}>{label}</p>
          <SidebarMenu>
            {items.map((item) => (
              <ChatHistoryItem
                href={buildItemHref(item.id)}
                isActive={item.isActive}
                isStreaming={chat.isStreaming && item.isActive}
                key={item.id}
                linkComponent={linkComponent}
                onDelete={() => onDeleteChat(item.id)}
                onSelect={onItemSelect}
                title={item.title}
              />
            ))}
          </SidebarMenu>
        </div>
      ))}
    </div>
  );
}
